/* =============================================================================
 * RosterNeedBand — the single presentation for a roster need (plan 3.1).
 * Consumed by SchoolCard (compact pill) and SchoolProfileModal (full band) so both
 * speak the same language. Token-driven; green/amber/red status scale + neutral.
 * ========================================================================== */
import React from 'react';
import { Flag } from 'react-feather';
import { RosterNeed } from '../utils/rosterNeed';

type Tone = RosterNeed['tone'];
const TONE: Record<Tone, { bg: string; border: string; text: string; icon: string; accent: string }> = {
  success: { bg: 'var(--aps-success-tint-2)', border: 'var(--aps-success-border)', text: 'var(--aps-success-text)', icon: 'var(--aps-success)', accent: 'var(--aps-success)' },
  warning: { bg: '#FEF3C7', border: '#FDE68A', text: '#B45309', icon: '#D97706', accent: '#F59E0B' },
  danger: { bg: '#FEE2E2', border: '#FECACA', text: '#B91C1C', icon: '#DC2626', accent: '#DC2626' },
  neutral: { bg: 'var(--aps-fill)', border: 'var(--aps-border)', text: 'var(--aps-text-muted)', icon: 'var(--aps-text-subtle)', accent: 'var(--aps-border)' },
};

export const RosterNeedBand: React.FC<{ need: RosterNeed | null; style?: React.CSSProperties; compact?: boolean }> = ({ need, style, compact }) => {
  if (!need) return null;
  const c = TONE[need.tone] || TONE.neutral;

  // Full-width status bar (school card footer): status color + the concrete detail,
  // spanning the whole card so nothing truncates. Sits as the visual footer.
  if (compact) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: c.bg,
          border: `1px solid ${c.border}`,
          borderRadius: 8,
          padding: '9px 12px',
          ...style,
        }}
      >
        <Flag size={14} color={c.icon} style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 12.5, color: c.text, lineHeight: 1.3 }}>
          <strong style={{ fontWeight: 800 }}>{need.headline}</strong>
          {need.sub ? ` · ${need.sub}` : ''}
        </span>
      </div>
    );
  }

  // Full band (school profile modal): a position+class match renders larger/stronger.
  const strong = !!need.strong && need.tone === 'success';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderLeft: strong ? `4px solid ${c.accent}` : undefined,
        borderRadius: 12,
        padding: strong ? '14px 16px' : '13px 15px',
        ...style,
      }}
    >
      <Flag size={strong ? 20 : 18} color={c.icon} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {strong && (
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: c.accent, marginBottom: 2 }}>
            Match for your class
          </div>
        )}
        <div style={{ fontSize: strong ? 17 : 15, fontWeight: 800, color: 'var(--aps-ink)', lineHeight: 1.2 }}>{need.headline}</div>
        {need.sub && (
          <div style={{ fontSize: 12.5, fontWeight: 600, color: c.text, marginTop: 1 }}>
            {need.sub}
          </div>
        )}
      </div>
    </div>
  );
};

export default RosterNeedBand;
